import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  BottomIcon,
  Button,
  Dropdown,
  IDropdownItem,
  Table,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import day from 'dayjs';
import { Column } from 'react-table';

import { AuthContext } from 'auth';
import { ClientsAvatar } from 'components';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import s from './ActiveClientsBlock.module.scss';
import {
  EPreset,
  ESort,
  IGetActiveClientsRequest,
  IGetActiveClientsResponse,
  IGetActiveWorkShift,
  TActiveClient,
} from './interfaces';
import { GET_ACTIVE_CLIENTS, GET_START_WORKSHIFT } from './schema';

export const ActiveClientsBlock: FC = () => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { me, permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const [isPresetsOpen, setIsPresetsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<EPreset>(EPreset.TODAY);

  const PRESETS: Array<IDropdownItem<EPreset>> = useMemo(
    () => [
      { value: EPreset.TODAY, title: t('clients.active.filters.day') },
      { value: EPreset.WEEK, title: t('clients.active.filters.week') },
      ...(me?.roles.some(
        (role) => role.alias === 'manager' || role.alias === 'owner'
      )
        ? [
            { value: EPreset.MONTH, title: t('clients.active.filters.month') },
            { value: EPreset.YEAR, title: t('clients.active.filters.year') },
          ]
        : []),
    ],
    [t, me]
  );

  const activePreset = PRESETS.find(
    (preset) => preset.value === selectedPreset
  );

  const [getActiveClients, { data, loading }] = useLazyQuery<
    IGetActiveClientsResponse,
    IGetActiveClientsRequest
  >(GET_ACTIVE_CLIENTS, {
    fetchPolicy: 'cache-and-network',
  });

  const [getActiveWorkShift] = useLazyQuery<IGetActiveWorkShift>(
    GET_START_WORKSHIFT,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const columns: Array<Column<TActiveClient>> = useMemo(
    () => [
      {
        id: 'nickname',
        Header: t('client.nickname'),
        accessor: ({ nickname, avatar_url, roles }) => ({
          nickname,
          avatar_url,
          roles,
        }),
        Cell: ({
          value: { nickname, avatar_url, roles },
        }: {
          value: {
            nickname: string;
            avatar_url: string;
            roles: { alias: string }[];
          };
        }) => {
          return (
            <div className={s.nickname}>
              <ClientsAvatar avatar={avatar_url} roles={roles} size="s" />
              <span>{nickname}</span>
            </div>
          );
        },
        width: 306,
      },
      {
        id: 'payments',
        Header: t('client.spends'),
        accessor: 'payments',
        Cell: ({ value }) => `${value?.toLocaleString() || 0} ${currency.sign}`,
      },
      {
        id: 'total_seconds',
        Header: t('client.totalHours'),
        accessor: 'total_seconds',
        Cell: ({ value }) =>
          value < 3600
            ? t('client.lowerHour')
            : t(
                'time.contracted.hours',
                (value / 3600)?.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                }) || 0
              ),
      },
      {
        id: 'last_client_activity',
        Header: t('client.lastVisit'),
        accessor: ({ last_client_activity }) =>
          last_client_activity ? day(last_client_activity).valueOf() : null,
        Cell: ({ value }: { value: number | null }) =>
          value ? day(value).format('D MMM YYYY') : '-',
        width: 153,
        disableResizing: true,
      },
    ],
    [currency.sign, t]
  );

  useEffect(() => {
    if (
      (!permissions?.has('client_manage') &&
        !permissions?.has('work_shift_active')) ||
      !permissions
    )
      return;

    if (
      !permissions.has('client_manage') &&
      permissions.has('work_shift_active')
    ) {
      getActiveWorkShift().then((activeWorkShiftData) => {
        if (!activeWorkShiftData.data) return;

        return getActiveClients({
          variables: {
            input: {
              sort: ESort.ByTime,
              from: activeWorkShiftData.data.activeWorkShift.created_at,
              to: day().format('YYYY-MM-DD HH:mm:ss'),
            },
            first: 5,
          },
        });
      });
    }

    if (permissions.has('client_manage')) {
      getActiveClients({
        variables: {
          input: { sort: ESort.ByTime, preset: selectedPreset },
          first: 5,
        },
      });
    }
  }, [permissions]);

  useEffect(() => {
    getActiveClients({
      variables: {
        input: { sort: ESort.ByTime, preset: selectedPreset },
        first: 5,
      },
    });
  }, [selectedPreset]);

  return (
    <Table
      headerBlock={
        <div className={s.header}>
          <span>
            {permissions?.has('client_manage')
              ? t('clients.active')
              : t('clients.active_workShift')}
          </span>
          {permissions?.has('client_manage') && (
            <div className={s.presets}>
              <Button
                size="s"
                variant="outline"
                className={s.presetButton}
                onClick={() => {
                  setIsPresetsOpen(!isPresetsOpen);
                }}
                disabled={loading}
              >
                {activePreset?.title}
                <BottomIcon size="s" />
              </Button>
              {isPresetsOpen && (
                <Dropdown
                  items={PRESETS}
                  onChange={setSelectedPreset}
                  close={() => {
                    setIsPresetsOpen(false);
                  }}
                  renderItem={(item) => (
                    <span
                      className={cn(
                        item.value === selectedPreset && s.selected
                      )}
                    >
                      {item.title}
                    </span>
                  )}
                  className={s.dropdown}
                />
              )}
            </div>
          )}
        </div>
      }
      sortBy={[{ id: 'payments', desc: true }]}
      emptyDataPlaceholder={t('clients.active.placeholder')}
      columns={columns}
      data={data?.getActiveClientList ?? []}
      className={s.table}
      isLoading={loading}
    />
  );
};
